import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
// Из лобби — пульсация кнопки старта (lobby-button-pulse) и бейдж «Вы»: здесь
// они должны выглядеть так же, как в сетевом лобби
import '../LobbyPage/LobbyPage.css';
import './HotSeatPlayersInitPage.css';
import {PLAYER_COLOR_INITIAL_NAME_MAPPING} from '../../configs/players';
import {BOT_NAMES} from '../../constants/botNames';
import {isEnvConfEnabled} from "../../utils/env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../../constants/env";
import {EMPTY_GAME_FIELD} from "../../configs/fields";
import {FINAL_ROUND} from "../../configs/game";
import PageButton from '../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import FactionSelectPage from '../pages/FactionSelectPage/FactionSelectPage';
import {selectFactionLoadouts} from '../../redux/reducers/privateStateReducer/selectors';
import {FACTION_CONFIGS, FACTION_IDS_IMPLEMENTED, RANDOM_FACTION_ID} from '../../configs/factions';
import {ALL_FACTION_SKINS} from '../../configs/factions/skins/allFactionSkins';
import PlayerFactionIcon from '../PlayerFactionIcon/PlayerFactionIcon';
import {getNextPlayerBonusType, PLAYER_BONUS_TYPES} from '../../configs/playerBonus';
import {useDesktopLayout} from '../../hooks/visuals/useWideScreenLayout';
import {
  DEMO_BOT_GAME_ACTIONS_SPEED_MAX_DELIMITER,
  HOT_SEAT_BOT_GAME_SPEED_LOCAL_STORAGE_KEY
} from '../../utils/bots/botConfig';
import {readHotSeatBotSpeed} from '../../utils/bots/readHotSeatBotSpeed';
import {getRandomElement} from '../../utils/other/getRandomElement';
import {getCachedOrFetch} from '../../utils/fetchCache';
import {API_BASE} from '../../utils/apiConfig';
import GroundBackgroundSelectButton from '../GroundBackgroundSelectButton/GroundBackgroundSelectButton';
import GroundBackgroundSelectPage from '../pages/GroundBackgroundSelectPage/GroundBackgroundSelectPage';
import {
  readHotSeatGroundBackground,
  writeHotSeatGroundBackground,
} from '../../utils/localMatch/hotSeatGroundBackgroundStorage';
import {buildFieldPreviewModel} from '../../utils/fieldPreview/buildFieldPreviewModel';
import HotSeatFieldCard from './HotSeatFieldCard';
import HotSeatDesktop, {getHotSeatSettingsHint} from './desktop/HotSeatDesktop';
import {
  HotSeatPlayerBonusToggle,
  HotSeatPlayerBotToggle,
  HotSeatPlayerNameField,
} from './HotSeatPlayerControls';

const SCREENS_HOT_SEAT_INIT = {
  MAIN: 'MAIN',
  // Настройки партии (раунды, зритель, таймер, биом, скорость ботов) — на
  // своем экране, чтобы на главном остались только игроки и старт
  SETTINGS: 'SETTINGS',
  FACTION_SELECT: 'FACTION_SELECT',
  GROUND_BACKGROUND_SELECT: 'GROUND_BACKGROUND_SELECT',
};

function HotSeatPlayersInitPage({field, onSubmit, onBack, onGoToCrystals, onOpenInventory, isMissionEditorMode = false}) {
  const [players, setPlayers] = useState([]);
  const [roundCount, setRoundCount] = useState(FINAL_ROUND);
  // Целочисленный множитель скорости хода ботов (×1..×MAX) в локальной игре,
  // запускаемой с этой страницы; сохраняется в localStorage и восстанавливается
  // при повторном открытии страницы (см. readHotSeatBotSpeed)
  const [botSpeed, setBotSpeed] = useState(readHotSeatBotSpeed);
  const [isSpectatorMode, setIsSpectatorMode] = useState(false);
  // Таймер хода в локальной игре — по умолчанию выключен, включается явно здесь
  const [isTimerEnabled, setIsTimerEnabled] = useState(false);
  // Планировщик ботов (волна 29, роллаут-выбор плана раунда) — только для
  // админов, по умолчанию выключен; действует на всех ботов этой партии
  const [isPlannerBotsEnabled, setIsPlannerBotsEnabled] = useState(false);
  // Подложка поля партии (игроку — «Биом»): идентификатор или null — случайная
  // (так было до появления выбора). Выбор запоминается в localStorage, как скорость ботов.
  // В редакторе миссий выбора нет: подложку миссии разыгрывает ее запуск, и
  // выбранная здесь жила бы только до конца разметки. Там значение так и
  // остается null: кнопки выбора нет, запомненный выбор не читается
  const [groundBackground, setGroundBackground] = useState(
    () => (isMissionEditorMode ? null : readHotSeatGroundBackground())
  );
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  // Содержимое файла выбранного поля — для его схемы рядом со списком игроков
  const [fieldData, setFieldData] = useState(null);
  const fieldPreviewModel = useMemo(() => buildFieldPreviewModel(fieldData), [fieldData]);
  // От этой ширины экран раскладывается как десктопное лобби, без прокрутки
  const isDesktopLayout = useDesktopLayout();
  const [screen, setScreen] = useState(SCREENS_HOT_SEAT_INIT.MAIN);
  const [factionSelectColor, setFactionSelectColor] = useState(null);
  const auth = useSelector((state) => state.private.auth);
  const factionLoadouts = useSelector(selectFactionLoadouts);
  // Скины, доступные РЕАЛЬНОМУ пользователю (куплены/получены) — пул, из
  // которого боты локальной игры получают случайный скин своей фракции
  // (см. handleSubmit). Рейтинговые (_gold/_diamond/_ruby) и notObtainable сюда не
  // попадают — их вообще нет в ALL_FACTION_SKINS/ответе /available-skins
  const [availableSkinIds, setAvailableSkinIds] = useState([]);

  usePageAnalytics('HotSeatPlayersInitPage');

  useEffect(() => {
    getCachedOrFetch('available-skins', () => fetch(`${API_BASE}/available-skins`, {
      method: 'GET',
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) throw new Error('Failed to fetch skins');
      return res.json();
    }))
      .then((data) => setAvailableSkinIds(data.skins || []))
      .catch((error) => console.error('[HotSeatPlayersInitPage] Failed to fetch available skins', error));
  }, []);

  // Пул фракций для «Случайно» в локальной игре: как и в сетевой — только
  // доступные (открытые) пользователю фракции; фолбэк на все реализованные —
  // только при полном отсутствии данных о владении (гость/оффлайн)
  const randomFactionIdsPool = useMemo(() => {
    const unlockedIds = new Set(
      (auth?.availableFactions || []).filter((faction) => faction.isAvailable).map((faction) => faction.id)
    );
    const availableIds = FACTION_IDS_IMPLEMENTED.filter((id) => unlockedIds.has(id));
    return availableIds.length > 0 ? availableIds : FACTION_IDS_IMPLEMENTED;
  }, [auth?.availableFactions]);

  const getUsedNames = (players) => new Set(
    players
      .filter((player) => BOT_NAMES.includes(player.name))
      .map((player) => player.name)
  );

  const getRandomBotName = (usedNames) => {
    const available = BOT_NAMES.filter((name) => !usedNames.has(name));
    if (available.length === 0) return null;
    return available[Math.floor(Math.random() * available.length)];
  };

  useEffect(() => {
    const loadField = async () => {
      try {
        const response = await fetch(field.path);
        const data = await response.json();
        setFieldData(data);
        const playerColors = data.playerColors || [];

        const usedNames = new Set();
        const players = playerColors.map((color, index) => {
          const isBot = index !== 0;
          let name;
          if (isBot) {
            name = getRandomBotName(usedNames);
            if (name) {
              usedNames.add(name);
            } else {
              name = `Бот ${PLAYER_COLOR_INITIAL_NAME_MAPPING[color]}`;
            }
          } else {
            name = auth?.name || PLAYER_COLOR_INITIAL_NAME_MAPPING[color];
            if (BOT_NAMES.includes(name)) {
              usedNames.add(name);
            }
          }
          return {
            color,
            name,
            isBot,
            factionId: null,
            bonus: PLAYER_BONUS_TYPES.NONE,
          }
        });

        setPlayers(players);
      } catch (error) {
        console.error('Error loading field for names:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadField();
  }, [field, auth?.name]);

  useEffect(() => {
    if (!isLoading) {
      // Автозапуск идет мимо формы, но запомненный выбор подложки действует и
      // тут: игрок назвал ее сам, и на пустом поле ждет ту же
      if (isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_AUTO_LOAD_TEST_FIELD)) {
        onSubmit(players, roundCount, false, false, false, groundBackground);
      } else if (field.id === EMPTY_GAME_FIELD.id) {
        onSubmit([], roundCount, false, false, false, groundBackground);
      }
    }
  }, [isLoading, players, onSubmit, field.id, roundCount, groundBackground]);

  useEffect(() => {
    try {
      localStorage.setItem(HOT_SEAT_BOT_GAME_SPEED_LOCAL_STORAGE_KEY, String(botSpeed));
    } catch {
      // localStorage недоступен (приватный режим и т.п.) — просто не сохраняем
    }
  }, [botSpeed]);

  const handleNameChange = (color, value) => {
    const nextPlayers = players.map((player) => {
      if (player.color === color) {
        return {
          ...player,
          name: value
        }
      } else {
        return player
      }
    })
    setPlayers(nextPlayers);
    if (value.trim().length >= 2) {
      setErrors(prev => ({...prev, [color]: false}));
    }
  };

  const handleBonusCycle = (color) => {
    setPlayers((prevPlayers) => prevPlayers.map((player) => (
      player.color === color ? {...player, bonus: getNextPlayerBonusType(player.bonus)} : player
    )));
  };

  // Режим зрителя включается ТОЛЬКО явным нажатием этой кнопки (партия
  // «все боты», собранная вручную тумблерами игроков, играется с обычным UI).
  // Включение переводит всех игроков в ботов; ручной перевод любого игрока
  // в «Человек» выключает режим, т.к. зрительская партия требует всех ботов
  const handleSpectatorToggle = () => {
    if (isSpectatorMode) {
      setIsSpectatorMode(false);
      return;
    }

    setIsSpectatorMode(true);
    setPlayers((prevPlayers) => {
      const usedNames = getUsedNames(prevPlayers);
      return prevPlayers.map((player) => {
        if (player.isBot) return player;
        const name = getRandomBotName(usedNames) || `Бот ${PLAYER_COLOR_INITIAL_NAME_MAPPING[player.color]}`;
        usedNames.add(name);
        return {
          ...player,
          isBot: true,
          name,
        };
      });
    });
  };

  const handleBotToggle = (color) => {
    const toggledPlayer = players.find((player) => player.color === color);
    if (toggledPlayer && toggledPlayer.isBot) {
      setIsSpectatorMode(false);
    }
    setPlayers((prevPlayers) => {
      const usedNames = getUsedNames(prevPlayers);
      return prevPlayers.map((player) => {
        if (player.color !== color) return player;
        const isBot = !player.isBot;
        let name = player.name;
        if (isBot) {
          name = getRandomBotName(usedNames) || `Бот ${PLAYER_COLOR_INITIAL_NAME_MAPPING[color]}`;
        } else {
          const isOnlyHuman = prevPlayers.every((p) => p.color === color || p.isBot);
          if (isOnlyHuman) {
            name = auth?.name || PLAYER_COLOR_INITIAL_NAME_MAPPING[color];
          }
        }
        return {
          ...player,
          isBot,
          name,
        }
      });
    });
  };

  const handleOpenFactionSelect = (color) => {
    setFactionSelectColor(color);
    setScreen(SCREENS_HOT_SEAT_INIT.FACTION_SELECT);
  };

  const handleFactionSelected = (factionId) => {
    // «Случайная фракция» в локальной игре — это существующий механизм
    // "Случайно": factionId = null резолвится в createPlayersState при старте
    const effectiveFactionId = factionId === RANDOM_FACTION_ID ? null : factionId;
    const nextPlayers = players.map((player) => (
      player.color === factionSelectColor ? {...player, factionId: effectiveFactionId} : player
    ));
    setPlayers(nextPlayers);
  };

  // Экраны настроек и выбора подложки показываются с самого верха, а экран,
  // с которого на них ушли, может быть длинным: без возврата прокрутки игрок
  // оказывался бы в случайном его месте и не видел то, что только что поменял
  // (на телефоне строка «Биом» уезжала за экран). Место запоминается на один
  // возврат к своему экрану
  const scrollYByScreenRef = useRef({});
  const openScreenFromCurrent = (nextScreen) => {
    scrollYByScreenRef.current[screen] = window.scrollY;
    setScreen(nextScreen);
  };
  useLayoutEffect(() => {
    const savedScrollY = scrollYByScreenRef.current[screen];
    if (savedScrollY !== undefined) {
      window.scrollTo(0, savedScrollY);
      delete scrollYByScreenRef.current[screen];
    } else if (screen === SCREENS_HOT_SEAT_INIT.SETTINGS) {
      // Кнопка «Настройки» — под списком игроков, и без этого экран настроек
      // открывался бы на телефоне прокрученным к низу
      window.scrollTo(0, 0);
    }
  }, [screen]);

  const handleOpenSettings = () => openScreenFromCurrent(SCREENS_HOT_SEAT_INIT.SETTINGS);
  const handleOpenGroundBackgroundSelect = () => openScreenFromCurrent(SCREENS_HOT_SEAT_INIT.GROUND_BACKGROUND_SELECT);

  const handleGroundBackgroundSelected = (groundBackgroundId) => {
    setGroundBackground(groundBackgroundId);
    writeHotSeatGroundBackground(groundBackgroundId);
    setScreen(SCREENS_HOT_SEAT_INIT.SETTINGS);
  };

  const handleRoundChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 100) {
      setRoundCount(value);
    }
  };

  const handleBotSpeedChange = (e) => {
    setBotSpeed(Number(e.target.value));
  };

  // Скины ботам локальной игры: случайно из скинов их фракции, доступных
  // РЕАЛЬНОМУ пользователю (куплены/получены + всегда доступный дефолтный),
  // без рейтинговых/notObtainable (их просто нет в ALL_FACTION_SKINS/пуле
  // availableSkinIds). Уже занятый кем-то скин той же фракции исключается из
  // пула следующего бота — аккаунтный скин игрока-человека (если он играет
  // той же фракцией) и скины уже обработанных ботов; при исчерпании пула —
  // откат на дефолтный (его могут делить сразу несколько ботов).
  // "Случайная" фракция (factionId: null) резолвится здесь же, а не позже в
  // createPlayersState — иначе для нее не от чего подбирать скин
  const assignBotSkins = (currentPlayers) => {
    const resolvedPlayers = currentPlayers.map((player) => ({
      ...player,
      factionId: player.factionId || getRandomElement(randomFactionIdsPool),
    }));

    const usedSkinIdsByFaction = {};
    const markSkinUsed = (factionId, skinId) => {
      if (!usedSkinIdsByFaction[factionId]) {
        usedSkinIdsByFaction[factionId] = new Set();
      }
      usedSkinIdsByFaction[factionId].add(skinId);
    };

    resolvedPlayers.forEach((player) => {
      if (player.isBot) return;
      const humanSkinId = factionLoadouts?.[player.factionId]?.selectedSkin;
      if (humanSkinId) {
        markSkinUsed(player.factionId, humanSkinId);
      }
    });

    return resolvedPlayers.map((player) => {
      if (!player.isBot) return player;

      const usedSkinIds = usedSkinIdsByFaction[player.factionId] || new Set();
      const candidateSkins = ALL_FACTION_SKINS.filter((skin) =>
        skin.factionId === player.factionId
        && !skin.notObtainable
        && availableSkinIds.includes(skin.id)
        && !usedSkinIds.has(skin.id)
      );

      if (candidateSkins.length === 0) {
        return {...player, selectedSkin: `${player.factionId}_default`};
      }

      const selectedSkin = getRandomElement(candidateSkins).id;
      markSkinUsed(player.factionId, selectedSkin);
      return {...player, selectedSkin};
    });
  };

  const handleSubmit = () => {
    const newErrors = {};
    let hasError = false;

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    onSubmit(
      assignBotSkins(players), roundCount, isSpectatorMode, isTimerEnabled, isPlannerBotsEnabled,
      groundBackground
    );
  };

  if (isLoading) {
    return (
      <h1 className="menu-title">Загрузка...</h1>
    );
  }

  if (players.length === 0) {
    return (
      <>
        <h1 className="menu-title">Имена игроков</h1>
        <p className="player-names-info">
          `В конфигурации поля ${field.id} не заданы игроки (playerColors)`
        </p>
      </>
    );
  }

  if (screen === SCREENS_HOT_SEAT_INIT.FACTION_SELECT) {
    const factionSelectPlayer = players.find((player) => player.color === factionSelectColor);

    return (
      <FactionSelectPage
        goToMainLobbyScreen={() => setScreen(SCREENS_HOT_SEAT_INIT.MAIN)}
        localMode
        actingPlayerFactionId={factionSelectPlayer?.factionId}
        onFactionSelected={handleFactionSelected}
        onGoToCrystals={onGoToCrystals}
        onOpenInventory={onOpenInventory}
      />
    );
  }

  if (screen === SCREENS_HOT_SEAT_INIT.GROUND_BACKGROUND_SELECT) {
    return (
      <GroundBackgroundSelectPage
        value={groundBackground}
        onSelect={handleGroundBackgroundSelected}
        onBack={() => setScreen(SCREENS_HOT_SEAT_INIT.SETTINGS)}
      />
    );
  }

  if (screen === SCREENS_HOT_SEAT_INIT.SETTINGS) {
    return (
      <>
        <h1 className="menu-title">Настройки матча</h1>

        <div className="round-input-container">
          <label className="round-label">Раунды:</label>
          <input
            type="number"
            className="round-input"
            value={roundCount}
            onChange={handleRoundChange}
            min="1"
            max="100"
          />
        </div>

        <button
          type="button"
          className={`spectator-mode-toggle ${isSpectatorMode ? 'spectator-mode-toggle_active' : ''}`}
          onClick={handleSpectatorToggle}
          title={isSpectatorMode
            ? 'Все игроки под управлением компьютера — вы наблюдаете за матчем со стороны. Нажмите, чтобы выключить.'
            : 'Все игроки переключатся под управление компьютера, а вы будете наблюдать за матчем со стороны.'}
        >
          Режим зрителя: {isSpectatorMode ? 'Вкл' : 'Выкл'}
        </button>

        <button
          type="button"
          className={`turn-timer-toggle ${isTimerEnabled ? 'turn-timer-toggle_active' : ''}`}
          onClick={() => setIsTimerEnabled((prev) => !prev)}
          title={isTimerEnabled
            ? 'На каждый ход отводится ограниченное время (как в обычном/рейтинговом матче). Нажмите, чтобы выключить.'
            : 'Включить ограничение времени на ход (как в обычном/рейтинговом матче).'}
        >
          Таймер хода: {isTimerEnabled ? 'Вкл' : 'Выкл'}
        </button>

        {!isMissionEditorMode && (
          <GroundBackgroundSelectButton
            value={groundBackground}
            onClick={handleOpenGroundBackgroundSelect}
            pageId="HotSeatPlayersInitPage"
          />
        )}

        {auth?.isAdmin && (
          <button
            type="button"
            className={`turn-timer-toggle ${isPlannerBotsEnabled ? 'turn-timer-toggle_active' : ''}`}
            onClick={() => setIsPlannerBotsEnabled((prev) => !prev)}
            title={isPlannerBotsEnabled
              ? 'Боты играют по новой архитектуре (планировщик раунда с роллаутами, волна 29). Ходы ботов заметно дольше. Нажмите, чтобы выключить.'
              : 'Включить всем ботам партии планировщик раунда (волна 29): сильнее на полях 4-5 игроков, но ходы ботов заметно дольше.'}
          >
            Планировщик ботов: {isPlannerBotsEnabled ? 'Вкл' : 'Выкл'}
          </button>
        )}

        <div className="bot-speed-container">
          <label className="round-label">Скорость хода ботов</label>
          <div className="bot-speed-slider volume-slider-container">
            <img
              className="volume-slider-icon bot-speed-slider-icon"
              src="/img/icons/upgrade.png"
              alt="Скорость хода ботов"
              title="Скорость хода ботов"
            />
            <input
              type="range"
              min="1"
              max={DEMO_BOT_GAME_ACTIONS_SPEED_MAX_DELIMITER}
              step="1"
              value={botSpeed}
              onChange={handleBotSpeedChange}
              className="volume-slider"
            />
            <span className="volume-slider-value">×{botSpeed}</span>
          </div>
        </div>

        <div className="field-buttons-container">
          <PageButton
            className="main-menu-button" onClick={() => setScreen(SCREENS_HOT_SEAT_INIT.MAIN)}
            pageId="HotSeatPlayersInitPage" buttonId="settings_back"
          >Назад</PageButton>
        </div>
      </>
    );
  }

  // Бейдж «Вы», как в лобби, — только когда живой игрок один: тогда понятно,
  // что это сам пользователь. При нескольких живых (игра за одним экраном)
  // «Вы» досталось бы кому-то одному наугад
  const humanPlayers = players.filter((player) => !player.isBot);
  const youPlayerColor = humanPlayers.length === 1 ? humanPlayers[0].color : null;

  if (isDesktopLayout) {
    return (
      <HotSeatDesktop
        field={field}
        fieldPreviewModel={fieldPreviewModel}
        players={players}
        youPlayerColor={youPlayerColor}
        factionLoadouts={factionLoadouts}
        errors={errors}
        isMissionEditorMode={isMissionEditorMode}
        settingsHint={getHotSeatSettingsHint({
          roundCount, isTimerEnabled, isSpectatorMode, groundBackground, isMissionEditorMode,
        })}
        onNameChange={handleNameChange}
        onBotToggle={handleBotToggle}
        onBonusCycle={handleBonusCycle}
        onOpenFactionSelect={handleOpenFactionSelect}
        onSubmit={handleSubmit}
        onOpenSettings={handleOpenSettings}
        onBack={onBack}
      />
    );
  }

  return (
    <>
      <h1 className="menu-title">Настройка игроков</h1>

      <div className="hot-seat-layout">
        {fieldPreviewModel && <HotSeatFieldCard field={field} model={fieldPreviewModel}/>}

        <div className="hot-seat-layout_main">
          <div className="player-names-list">
            {/* На планшете игрок — одна строка контролов, и списку хватает общего
                заголовка; на телефоне строка разваливается на несколько, и каждый
                игрок получает свой («Игрок N:», см. .player-name-row-title) */}
            <div className="round-label player-names-list-title">Игроки:</div>
            {players.map(({color, name, isBot, factionId, bonus}, index) => {
              const factionConfig = factionId ? FACTION_CONFIGS.find((f) => f.id === factionId) : null;
              const isLocalBot = isBot && !isMissionEditorMode;

              return (
                <div key={color} className="player-name-row">
                  <div className="round-label player-name-row-title">Игрок {index + 1}:</div>
                  <HotSeatPlayerNameField
                    color={color}
                    name={name}
                    hasError={Boolean(errors[color])}
                    isYou={color === youPlayerColor}
                    onChange={handleNameChange}
                  />
                  <button
                    type="button"
                    className="player-faction-select-button"
                    onClick={() => handleOpenFactionSelect(color)}
                    title={factionConfig ? `Фракция: ${factionConfig.name}. Нажмите, чтобы изменить.` : 'Фракция будет выбрана случайно. Нажмите, чтобы выбрать самостоятельно.'}
                  >
                    <PlayerFactionIcon
                      player={{color}}
                      factionId={factionId}
                      skinId={factionId ? factionLoadouts?.[factionId]?.selectedSkin : undefined}
                      className="player-faction-select-icon"
                    />
                    <span className="player-faction-select-name">
                      {factionConfig ? factionConfig.name : 'Случайно'}
                    </span>
                  </button>
                  <HotSeatPlayerBotToggle
                    color={color} isBot={isBot} isLocalBot={isLocalBot} bonus={bonus} onToggle={handleBotToggle}
                  />
                  <HotSeatPlayerBonusToggle
                    color={color} bonus={bonus} isLocalBot={isLocalBot} onCycle={handleBonusCycle}
                  />
                  {errors[color] && (
                    <span className="player-name-error-text">Минимум 2 символа</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="field-buttons-container">
            {/* Как «Начать матч» в лобби, когда все готовы: красная и пульсирует —
                здесь игроки готовы всегда */}
            <PageButton
              className="field-select-button field-select-button_start-game hot-seat-start-button lobby-button-pulse"
              onClick={handleSubmit}
              pageId="HotSeatPlayersInitPage" buttonId="start_game"
            >
              Начать матч
            </PageButton>

            <PageButton
              className="main-menu-button" onClick={handleOpenSettings} pageId="HotSeatPlayersInitPage" buttonId="settings"
            >Настройки</PageButton>

            <PageButton
              className="main-menu-button" onClick={onBack} pageId="HotSeatPlayersInitPage" buttonId="back"
            >Назад</PageButton>
          </div>
        </div>
      </div>
    </>
  );
}

export default HotSeatPlayersInitPage;
